import { commodityConfig } from '../config/commodityConfig.js';

export function categorizeCommodity(commodityName) {
  if (!commodityName) {
    return {
      category: 'unknown',
      legendGroup: 'unknown',
      display: 'Unknown'
    };
  }

  const normalizedName = commodityName.toLowerCase().trim();

  const highValueMinerals = commodityConfig?.HIGH_VALUE_MINERALS || [];
  const otherMinerals = commodityConfig?.OTHER_MINERALS || [];
  const nonCriticalMinerals = commodityConfig?.NON_CRITICAL_MINERALS || [];

  const isHighValue = highValueMinerals.some(mineral =>
    mineral.toLowerCase() === normalizedName
  );

  if (isHighValue) {
    return {
      category: 'critical',
      legendGroup: 'high-value',
      display: commodityName
    };
  }

  const isOtherCritical = otherMinerals.some(mineral =>
    mineral.toLowerCase() === normalizedName
  );

  if (isOtherCritical) {
    return {
      category: 'critical',
      legendGroup: 'other',
      display: commodityName
    };
  }

  const isNonCritical = nonCriticalMinerals.some(mineral =>
    mineral.toLowerCase() === normalizedName
  );

  if (isNonCritical) {
    return {
      category: 'non-critical',
      legendGroup: 'non-critical',
      display: commodityName
    };
  }

  return {
    category: 'unknown',
    legendGroup: 'unknown',
    display: commodityName
  };
}

export function processMines(mines, commodityMap, countryMap) {
  if (!mines || !Array.isArray(mines)) {
    return [];
  }

  return mines
    .filter(mine =>
      mine.latitude &&
      mine.longitude &&
      (mine.commodity_id !== null && mine.commodity_id !== undefined || mine.commodity)
    )
    .map(mine => {
      // Extract commodity name from nested object first, then fallback to commodityMap
      const commodityName = mine.commodity?.commodity_name ||
                           commodityMap[mine.commodity_id] ||
                           'Unknown';

      const commodityCategory = categorizeCommodity(commodityName);

      return {
        ...mine,
        commodity: commodityName,
        commodity_display: commodityCategory.display,
        commodity_category: commodityCategory.category,
        legend_group: commodityCategory.legendGroup,
        country_name: mine.country?.country_name || countryMap[mine.country_id] || 'Unknown'
      };
    });
}

export function normalizeData(data) {
  if (!data) return null;

  if (data.type === 'FeatureCollection' && data.features) {
    return data;
  }

  if (data.mines) {
    return {
      type: 'FeatureCollection',
      features: data.mines.map(mine => ({
        type: 'Feature',
        geometry: {
          type: 'Point',
          coordinates: [mine.longitude, mine.latitude]
        },
        properties: mine
      }))
    };
  }

  if (Array.isArray(data)) {
    return {
      type: 'FeatureCollection',
      features: data.map(item => {
        if (item.type === 'Feature') {
          return item;
        }
        return {
          type: 'Feature',
          geometry: {
            type: 'Point',
            coordinates: [item.longitude, item.latitude]
          },
          properties: item
        };
      })
    };
  }

  return data;
}

export function translateFiltersForAPI(filters, commodities) {
  const apiFilters = { ...filters };

  if (filters.commodity_category) {
    if (!commodities || commodities.length === 0) {
      console.warn('Commodities not loaded yet, skipping commodity_category filter translation');
      delete apiFilters.commodity_category;
      return apiFilters;
    }

    const selectedCommodityIds = [];

    filters.commodity_category.forEach(category => {
      if (category === 'critical') {
        const criticalMinerals = [
          ...(commodityConfig?.HIGH_VALUE_MINERALS || []),
          ...(commodityConfig?.OTHER_MINERALS || [])
        ];

        commodities.forEach(commodity => {
          const normalizedName = commodity.commodity_name.toLowerCase();
          if (criticalMinerals.some(mineral => mineral.toLowerCase() === normalizedName)) {
            selectedCommodityIds.push(commodity.commodity_id);
          }
        });
      } else if (category === 'non-critical') {
        const nonCriticalMinerals = commodityConfig?.NON_CRITICAL_MINERALS || [];

        commodities.forEach(commodity => {
          const normalizedName = commodity.commodity_name.toLowerCase();
          if (nonCriticalMinerals.some(mineral => mineral.toLowerCase() === normalizedName)) {
            selectedCommodityIds.push(commodity.commodity_id);
          }
        });
      }
    });

    if (selectedCommodityIds.length > 0) {
      apiFilters.commodity_ids = selectedCommodityIds;
    }

    delete apiFilters.commodity_category;
  }

  return apiFilters;
}

export function calculateRiskLevel(mine) {
  if (typeof FragileStatesModule !== 'undefined' && FragileStatesModule.getRiskLevel) {
    return FragileStatesModule.getRiskLevel(mine.country_name);
  }

  const defaultRiskLevels = {
    'United States': 'Very Low',
    'Canada': 'Very Low',
    'Australia': 'Low',
    'United Kingdom': 'Low',
    'Germany': 'Low',
    'France': 'Low',
    'Japan': 'Low',
    'South Korea': 'Low',
    'Brazil': 'Medium',
    'Mexico': 'Medium',
    'India': 'Medium',
    'China': 'Medium',
    'Russia': 'Medium',
    'South Africa': 'Medium',
    'Chile': 'Low',
    'Peru': 'Medium',
    'Argentina': 'Medium',
    'Colombia': 'High',
    'Venezuela': 'Very High',
    'Democratic Republic of the Congo': 'Very High',
    'Afghanistan': 'Very High',
    'Somalia': 'Very High',
    'Yemen': 'Very High',
    'Syria': 'Very High',
    'Libya': 'High',
    'Iraq': 'High',
    'Nigeria': 'High',
    'Pakistan': 'High',
    'Myanmar': 'High',
    'Mali': 'Very High',
    'Burkina Faso': 'Very High',
    'Niger': 'Very High',
    'Central African Republic': 'Very High',
    'Chad': 'Very High',
    'Sudan': 'Very High',
    'South Sudan': 'Very High',
    'Eritrea': 'Very High',
    'Ethiopia': 'High',
    'Mozambique': 'High',
    'Tanzania': 'Medium',
    'Zimbabwe': 'High',
    'Guinea': 'High',
    'Sierra Leone': 'High'
  };

  return defaultRiskLevels[mine.country_name] || 'Unknown';
}

export function aggregateMinesByCategory(mines) {
  const categories = {};

  mines.forEach(mine => {
    const category = mine.commodity_category || 'unknown';
    if (!categories[category]) {
      categories[category] = {
        count: 0,
        mines: []
      };
    }
    categories[category].count++;
    categories[category].mines.push(mine);
  });

  return categories;
}

export function aggregateMinesByCountry(mines) {
  const countries = {};

  mines.forEach(mine => {
    const country = mine.country_name || 'Unknown';
    if (!countries[country]) {
      countries[country] = {
        count: 0,
        mines: []
      };
    }
    countries[country].count++;
    countries[country].mines.push(mine);
  });

  return countries;
}

export function aggregateMinesByStatus(mines) {
  const statuses = {};

  mines.forEach(mine => {
    const status = mine.mine_status || 'Unknown';
    if (!statuses[status]) {
      statuses[status] = {
        count: 0,
        mines: []
      };
    }
    statuses[status].count++;
    statuses[status].mines.push(mine);
  });

  return statuses;
}

export function sortMinesByRisk(mines) {
  const riskOrder = {
    'Very High': 5,
    'High': 4,
    'Medium': 3,
    'Low': 2,
    'Very Low': 1,
    'Unknown': 0
  };

  return mines.sort((a, b) => {
    const aRisk = riskOrder[a.risk_level] || 0;
    const bRisk = riskOrder[b.risk_level] || 0;
    return bRisk - aRisk;
  });
}

export function filterMinesByViewport(mines, bounds) {
  if (!bounds) return mines;

  return mines.filter(mine => {
    const lng = parseFloat(mine.longitude);
    const lat = parseFloat(mine.latitude);

    return lng >= bounds.west &&
           lng <= bounds.east &&
           lat >= bounds.south &&
           lat <= bounds.north;
  });
}

export function getUniqueValues(mines, property) {
  const values = new Set();
  mines.forEach(mine => {
    if (mine[property]) {
      values.add(mine[property]);
    }
  });
  return Array.from(values).sort();
}